import { themeColor } from "../../../../types/color";
import type {
  ConversationStartersPreset,
  ConversationStartersPresetStyle,
  ConversationStartersWidgetState,
} from "./types";

/**
 * Preset definitions derived from Figma mockups.
 * Each preset maps to a specific Figma design variant:
 * - inline: V1 (1716:23989) — pills always visible, sparkle icon, light borders
 * - highlighted: V2 (1716:24128) — gray bg container, sparkle icon, dark fully-rounded pills
 * - accordion: V3 (1716:24267 / 1716:24527) — collapsible with chevron, no sparkle
 * - accordion-highlighted: V4 (1716:24396) — accordion + highlighted background
 * - pages: the concierge question chips of a Pages product page — plain title, outline pills
 */
export const CONVERSATION_STARTERS_PRESETS: Record<
  Exclude<ConversationStartersPreset, "custom">,
  ConversationStartersPresetStyle
> = {
  inline: {
    showTitle: true,
    showSparkleIcon: true,
    collapsible: false,
    titleFontSize: 20,
    titleFontWeight: "semibold",
    titleColor: themeColor("textPrimary"),
    containerGap: 16,
    containerMarginTop: 16,
    containerMarginBottom: 16,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 0,
    containerPaddingY: 0,
    containerBorderRadius: 0,
    pillPaddingX: 12,
    pillPaddingY: 4,
    pillBorderRadius: 50,
    pillBorderColor: themeColor("primary"),
    pillBorderWidth: 1.5,
    pillBgColor: themeColor("background"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 16,
    pillFontWeight: "medium",
  },
  highlighted: {
    showTitle: true,
    showSparkleIcon: true,
    collapsible: false,
    titleFontSize: 20,
    titleFontWeight: "semibold",
    titleColor: themeColor("textPrimary"),
    containerGap: 16,
    containerMarginTop: 16,
    containerMarginBottom: 16,
    containerBgColor: themeColor("background"),
    containerPaddingX: 16,
    containerPaddingY: 16,
    containerBorderRadius: 0,
    pillPaddingX: 12,
    pillPaddingY: 4,
    pillBorderRadius: 50,
    pillBorderColor: themeColor("primary"),
    pillBorderWidth: 1.5,
    pillBgColor: themeColor("background"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 16,
    pillFontWeight: "medium",
  },
  accordion: {
    showTitle: true,
    showSparkleIcon: false,
    collapsible: true,
    titleFontSize: 20,
    titleFontWeight: "semibold",
    titleColor: themeColor("textPrimary"),
    containerGap: 16,
    containerMarginTop: 16,
    containerMarginBottom: 16,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 0,
    containerPaddingY: 0,
    containerBorderRadius: 0,
    pillPaddingX: 12,
    pillPaddingY: 4,
    pillBorderRadius: 50,
    pillBorderColor: themeColor("primary"),
    pillBorderWidth: 1.5,
    pillBgColor: themeColor("background"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 16,
    pillFontWeight: "medium",
  },
  "accordion-highlighted": {
    showTitle: true,
    showSparkleIcon: false,
    collapsible: true,
    titleFontSize: 20,
    titleFontWeight: "semibold",
    titleColor: themeColor("textPrimary"),
    containerGap: 16,
    containerMarginTop: 16,
    containerMarginBottom: 16,
    containerBgColor: themeColor("background"),
    containerPaddingX: 16,
    containerPaddingY: 16,
    containerBorderRadius: 0,
    pillPaddingX: 12,
    pillPaddingY: 4,
    pillBorderRadius: 50,
    pillBorderColor: themeColor("primary"),
    pillBorderWidth: 1.5,
    pillBgColor: themeColor("background"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 16,
    pillFontWeight: "medium",
  },
  pages: {
    showTitle: true,
    showSparkleIcon: false,
    collapsible: false,
    titleFontSize: 20,
    titleFontWeight: "normal",
    titleColor: themeColor("textPrimary"),
    containerGap: 16,
    containerMarginTop: 16,
    containerMarginBottom: 16,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 0,
    containerPaddingY: 0,
    containerBorderRadius: 0,
    pillPaddingX: 20,
    pillPaddingY: 6,
    pillBorderRadius: 50,
    pillBorderColor: themeColor("textPrimary"),
    pillBorderWidth: 1.5,
    pillBgColor: themeColor("background"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 16,
    pillFontWeight: "medium",
  },
};

/** The widget with no saved config: the inline preset, and no "Ask anything" pill. */
export const CONVERSATION_STARTERS_DEFAULT_STATE: ConversationStartersWidgetState = {
  preset: "inline",
  ...CONVERSATION_STARTERS_PRESETS.inline,
  titleText: "Get expert advice on this product",
  titleFontFamily: "inherit",
  pillFontFamily: "inherit",
  showAskAnything: false,
  askAnythingLabel: "Ask anything",
};
